"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@/module_bindings";
import { mapMember, type OrgUserRow } from "@/lib/stdb-map";
import { useResolvedCurrentOrganization } from "./use-current-organization";
import type { Member } from "@/types/member";

/**
 * `org_members` ⋈ `org_users`, filtered client-side to `orgId` (the views
 * already isolate rows to what the caller can see across ALL their orgs;
 * this hook picks out just the one org).
 */
export function useMembers(orgId?: string): {
  members: Member[];
  isReady: boolean;
} {
  const current = useResolvedCurrentOrganization();
  const effectiveOrgId = orgId ?? current.organization?.id;

  const [memberRows, membersReady] = useTable(tables.org_members);
  const [userRows, usersReady] = useTable(tables.org_users);

  const usersById = useMemo(() => {
    const map = new Map<string, OrgUserRow>();
    for (const row of userRows) map.set(row.id, row);
    return map;
  }, [userRows]);

  const members = useMemo(() => {
    if (!effectiveOrgId) return [];
    return memberRows
      .filter((row) => row.organizationId === effectiveOrgId)
      .map((row) => mapMember(row, usersById));
  }, [memberRows, usersById, effectiveOrgId]);

  const isReady = orgId
    ? membersReady && usersReady
    : membersReady && usersReady && current.isReady;

  return { members, isReady };
}
